import './styles.scss';

import {
  AccidentHistoryFormDefinitions,
  AccidentIssueWithDocuments,
} from '@/features/AccidentHistory/components/AccidentHistoryForm/types';
import { DispatchIssueWithDocuments, IssueHistoryFormDefinitions } from '@/features/IssueHistory/components/IssueHistoryForm/types';
import DynamicContent, { DynamicContentAs } from '@/components/DynamicContent';
import {
  IncidentHistoryFormDefinitions,
  IncidentIssueWithDocuments,
} from '@/features/IncidentHistory/components/IncidentHistoryForm/types';
import {
  LostAndFoundHistoryFormDefinitions,
  LostAndFoundIssueWithDocuments,
} from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm/types';
import { UseTripViewState, initUseTripsModalState, useTripViewState } from '@/features/Trips/components/TripModal/views/trip/hook';

import AccidentHistoryForm from '@/features/AccidentHistory/components/AccidentHistoryForm';
import ActivityView from '@/features/Trips/components/TripModal/views/activity';
import IncidentHistoryForm from '@/features/IncidentHistory/components/IncidentHistoryForm';
import IssueHistoryForm from '@/features/IssueHistory/components/IssueHistoryForm';
import LostAndFoundHistoryForm from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm';
import { DispatchIssueWithCacheFlightTypeEnum, Trip, TripKindEnum } from '@/models/gen/graphql';
import TripView from '@/features/Trips/components/TripModal/views/trip';
import { createComponentState } from '@/state';
import equal from 'fast-deep-equal/es6/react';
import { getFormStorage } from '@/common/Form';
import useConfirmation from '@/hooks/useConfirmation';
import { useMemo } from 'react';
import useSession from '@/state/session';

export type TripModalTab =
  | 'trip'
  | 'flag'
  | 'communication'
  | 'completion'
  | 'activity'
  | 'fcr'
  | 'combine'
  | 'rate'
  | 'incident'
  | 'accident'
  | 'lostAndFound'
  | 'dispatchIssue';
export type TripModalState = {
  as?: DynamicContentAs;
  show: boolean;
  data: Partial<Trip>;
  accident?: Partial<AccidentIssueWithDocuments>;
  incident?: Partial<IncidentIssueWithDocuments>;
  lostAndFound?: Partial<LostAndFoundIssueWithDocuments>;
  dispatchIssue?: Partial<DispatchIssueWithDocuments>;
  tab?: TripModalTab;
  selected?: Partial<Trip>[];
  onSubmit?: (state: any) => void;
};
export type TripModalViewProps = {
  as?: DynamicContentAs;
  data: Partial<Trip>;
  title?: string;
  mode?: 'editing' | 'creating';
  selected?: Partial<Trip>[];
  onSubmit?: (state: UseTripViewState, persist?: boolean) => void;
  onHide?: (isCancel?: boolean, redirect?: boolean) => Promise<boolean>;
};
const clearFormStorage = () => {
  AccidentHistoryFormDefinitions.clearForm();
  IncidentHistoryFormDefinitions.clearForm();
  LostAndFoundHistoryFormDefinitions.clearForm();
  IssueHistoryFormDefinitions.clearForm();
};
const initState: TripModalState = {
  as: 'drawer',
  show: false,
  data: null,
  accident: null,
  incident: null,
  lostAndFound: null,
  dispatchIssue: null,
  tab: 'trip',
  selected: [],
};
export const useTripModalState = createComponentState(initState);

const TripModal = (): JSX.Element => {
  const state = useTripModalState(({ state }) => state);
  const setState = useTripModalState(({ setState }) => setState);
  const original = useTripViewState(({ state }) => state?.original);
  const trip = useTripViewState(({ state }) => state?.trip);
  const setViewState = useTripViewState(({ setState }) => setState);
  const { as, show, data, tab, onSubmit, ...rest } = state;
  const isEditing = !!data?.id;

  const confirmHide = useConfirmation({
    Body: {
      message: (
        <>
          <span>Are you sure?</span>
          <br />
          <small>Any unsaved changes will be lost.</small>
        </>
      ),
    },
  });

  const onClickModalTab = async (tabName: TripModalTab) => {
    try {
      if (!equal(original, trip)) await confirmHide();
      setViewState((current) => ({ ...current, trip: original }));
      setState((current) => ({ ...current, tab: tabName }));
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleSubmit = (state: any, persist: boolean = false) => {
    onSubmit?.(state);
    clearFormStorage();
    if (!persist) onHide(false);
  };
  const onHide = async (isCancel: boolean = true, redirect: boolean = false): Promise<boolean> => {
    try {
      if (redirect) {
        setState(initState);
        setViewState(initUseTripsModalState);
        clearFormStorage();
        return true;
      }
      const issuesValue = Object.assign(
        {},
        AccidentHistoryFormDefinitions.loadForm(),
        IncidentHistoryFormDefinitions.loadForm(),
        LostAndFoundHistoryFormDefinitions.loadForm(),
        IssueHistoryFormDefinitions.loadForm()
      );
      const issuesHasValue = Object.keys(issuesValue).length > 0;
      if ((!equal(original, trip) || issuesHasValue) && isCancel) await confirmHide();
      setState(initState);
      setViewState(initUseTripsModalState);
      clearFormStorage();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const [View, title] = useMemo(() => {
    const DefaultView = () => <></>;
    switch (tab) {
      case 'trip':
        return [TripView, isEditing ? 'Edit Trip' : 'Create Trip'];
      case 'activity':
        return [ActivityView, 'Activity'];
      case 'incident':
        return [IncidentFormView, 'Incident'];
      case 'accident':
        return [AccidentFormView, 'Accident'];
      case 'lostAndFound':
        return [LostAndFoundFormView, 'Lost And Found'];
      case 'dispatchIssue':
        return [DispatchIssueFormView, 'Dispatch Summary'];
      default:
        return [DefaultView];
    }
  }, [tab, isEditing]);

  const createTabOption = (iconName, tooltip, tabName) => ({
    icon: <i className={`sv sv-${iconName}`} />,
    tooltip,
    onClick: () => onClickModalTab(tabName),
    active: tab === tabName,
  });

  const tabOptions = isEditing
    ? [
        createTabOption('plane', 'Trip', 'trip'),
        createTabOption('bubble-dots', 'Activity', 'activity'),
        //createTabOption('flag-empty', 'Flags', 'flag'),
        //createTabOption('message2', 'Communications', 'communication'),
        //createTabOption('completion', 'Completion', 'completion'),
        //createTabOption('fcr', 'Complaints', 'fcr'),
        createTabOption('ambulance', 'Report Accident', 'accident'),
        createTabOption('shield-alert', 'Report Incident', 'incident'),
        createTabOption('box', 'Report Lost Item', 'lostAndFound'),
        //createTabOption('combine-icon', 'Combine', 'combine'),
        //createTabOption('bag-dollar', 'Rate', 'rate'),
        createTabOption('headset', 'Report Dispatch Issue', 'dispatchIssue'),
      ]
    : undefined;

  return (
    <DynamicContent
      className={`TripModal p-3`}
      show={show}
      drawer
      modal
      as="drawer"
      onHide={onHide}
      currentTab={tab}
      options={{
        modal: { props: { style: { top: 15 }, tabs: tabOptions, modalType: isEditing ? 'editingTrip' : 'createTrip' } },
        drawer: {
          props: {
            width: 560,
            tabs: tabOptions,
          },
        },
      }}
    >
      {data && (
        <View data={data} title={title} mode={data?.id ? 'editing' : 'creating'} onSubmit={handleSubmit} onHide={onHide} {...rest} />
      )}
    </DynamicContent>
  );
};

const IncidentFormView = ({ data, onHide }: TripModalViewProps): JSX.Element => {
  const { user } = useSession(({ state }) => state);
  const [incident, setState] = useTripModalState(({ state, setState }) => [state.incident || {}, setState]);
  const issue = useMemo(
    (): IncidentHistoryFormDefinitions.Value => ({
      ...getFormStorage('IncidentHistoryForm'),
      tripId: data?.id || '',
      tripFlightNumber: data?.flightNumber || 0,
      tripLoopName: data?.loopName || '',
      station: data?.airportCode || '',
      tripServicerIataAirlineCode: data?.servicerIataAirlineCode || '',
      driverId: data?.driverId || '',
      driverFirstName: data?.driver?.firstName || '',
      driverLastName: data?.driver?.lastName || '',
      vehicleId: data?.vehicleId || '',
      vehicleTrackingId: data?.vehicle?.trackingId || '',
      tripScheduled: data?.scheduled || '',
      documents: incident.documents || [],
      ...incident,
    }),
    [data, incident]
  );
  return (
    <IncidentHistoryForm
      value={{ ...issue, dispatcherId: issue.dispatcherId ? issue.dispatcherId : user.role?.name === 'dispatch' ? user.id : '' }}
      trip={data}
      onSubmit={() => {
        clearFormStorage();
        onHide();
      }}
      onEditIncident={(incident) => {
        IncidentHistoryFormDefinitions.clearForm();
        setState((current) => ({
          ...current,
          incident,
        }));
      }}
      key={incident?.id || data?.id}
    />
  );
};

const AccidentFormView = ({ data, onHide }: TripModalViewProps): JSX.Element => {
  const { user } = useSession(({ state }) => state);
  const [accident, setState] = useTripModalState(({ state, setState }) => [state.accident || {}, setState]);
  const issue = useMemo(
    (): AccidentHistoryFormDefinitions.Value => ({
      ...getFormStorage('AccidentHistoryForm'),
      tripId: data?.id || '',
      tripFlightNumber: data?.flightNumber || 0,
      tripLoopName: data?.loopName || '',
      tripScheduled: data?.scheduled || '',
      stationText: data?.airportCode || '',
      tripServicerIataAirlineCode: data?.servicerIataAirlineCode || '',
      driverId: data?.driverId || '',
      driverFirstName: data?.driver?.firstName || '',
      driverLastName: data?.driver?.lastName || '',
      vehicleId: data?.vehicleId || '',
      vehicleTrackingId: data?.vehicle?.trackingId || '',
      documents: accident.documents || [],
      ...accident,
    }),
    [data, accident]
  );
  return (
    <AccidentHistoryForm
      value={{ ...issue, dispatcherId: issue.dispatcherId ? issue.dispatcherId : user.role?.name === 'dispatch' ? user.id : '' }}
      trip={data}
      onSubmit={() => {
        clearFormStorage();
        onHide();
      }}
      onEditAccident={(accident) => {
        AccidentHistoryFormDefinitions.clearForm();
        setState((current) => ({
          ...current,
          accident,
        }));
      }}
      key={accident?.id || data?.id}
    />
  );
};

const LostAndFoundFormView = ({ data, onHide }: TripModalViewProps): JSX.Element => {
  const { user } = useSession(({ state }) => state);
  const [lostAndFound, setState] = useTripModalState(({ state, setState }) => [state.lostAndFound || {}, setState]);
  const issue = useMemo(
    (): LostAndFoundHistoryFormDefinitions.Value => ({
      ...getFormStorage('LostAndFoundHistoryForm'),
      tripId: data?.id || '',
      tripFlightNumber: data?.flightNumber || 0,
      tripLoopName: data?.loopName || '',
      tripAirportCode: data?.airportCode || '',
      tripServicerIataAirlineCode: data?.servicerIataAirlineCode || '',
      dispatcherId: (lostAndFound.dispatcherId ?? user.role?.name === 'dispatch') ? user.id : '',
      driverId: data?.driverId || '',
      driverFirstName: data?.driver?.firstName || '',
      driverLastName: data?.driver?.lastName || '',
      vehicleId: data?.vehicleId || '',
      vehicleTrackingId: data?.vehicle?.trackingId || '',
      tripScheduled: data?.scheduled || '',
      documents: lostAndFound.documents || [],
      ...lostAndFound,
    }),
    [data, lostAndFound]
  );
  return (
    <LostAndFoundHistoryForm
      value={issue}
      trip={data}
      onSubmit={() => {
        clearFormStorage();
        onHide();
      }}
      onEditLostAndFound={(lostAndFound) => {
        LostAndFoundHistoryFormDefinitions.clearForm();
        setState((current) => ({
          ...current,
          lostAndFound,
        }));
      }}
      key={lostAndFound?.id || data?.id}
    />
  );
};

const DispatchIssueFormView = ({ data, onHide }: TripModalViewProps): JSX.Element => {
  const { user } = useSession(({ state }) => state);
  const [dispatchIssue, setState] = useTripModalState(({ state, setState }) => [state.dispatchIssue || {}, setState]);
  let flightType: DispatchIssueWithCacheFlightTypeEnum = undefined;
  switch (data?.kind) {
    case TripKindEnum.Arr:
      flightType = DispatchIssueWithCacheFlightTypeEnum.Arr;
    case TripKindEnum.Dep:
      flightType = DispatchIssueWithCacheFlightTypeEnum.Dep;
  }
  const issue = useMemo(
    (): IssueHistoryFormDefinitions.Value => ({
      ...getFormStorage('DispatchIssueHistoryForm'),
      tripId: data?.id || '',
      tripFlightNumber: data?.flightNumber || 0,
      tripLoopName: data?.loopName || '',
      station: data?.airportCode || '',
      tripServicerIataAirlineCode: data?.servicerIataAirlineCode || '',
      driverId: data?.driverId || '',
      driverFirstName: data?.driver?.firstName || '',
      driverLastName: data?.driver?.lastName || '',
      tripScheduled: data?.scheduled || '',
      scheduledTime: data?.scheduled || undefined,
      vehicleId: data?.vehicleId || '',
      flightType,
      documents: dispatchIssue?.documents || [],
      ...dispatchIssue,
    }),
    [data, dispatchIssue]
  );
  return (
    <IssueHistoryForm
      value={{ ...issue, dispatcherId: issue.dispatcherId ? issue.dispatcherId : user.role?.name === 'dispatch' ? user.id : '' }}
      trip={data}
      onSubmit={() => {
        clearFormStorage();
        onHide();
      }}
      onEditDispatchIssue={(dispatchIssue) => {
        IssueHistoryFormDefinitions.clearForm();
        setState((current) => ({
          ...current,
          dispatchIssue,
        }));
      }}
      key={dispatchIssue?.id || data?.id}
    />
  );
};

export default TripModal;
